import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, LinearProgress } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { FileDto } from './Document.interface';
import { useDeleteDocument } from './Document.hook';
import { FetchError } from '../../common/api/api';

export interface DeleteDialogProps {
  document: FileDto;
  open: boolean;
  onCancel: () => void;
  onSuccess: (file: FileDto, message: string) => void;
  onError?: (error: FetchError, errorMessage: string) => void;
}

const DocumentDeleteDialog: React.FC<DeleteDialogProps> = ({
  document,
  open,
  onCancel,
  onSuccess,
  onError
}) => {
  const classes = useStyles();
  const isFolder = document.isFolder;
  const { data: deletedDocuments, isLoading, error, del } = useDeleteDocument(
    document.id
  );

  useEffect(() => {
    if (deletedDocuments) {
      const deletedDocument = Array.isArray(deletedDocuments)
        ? deletedDocuments[0]
        : deletedDocuments;
      const action = document.path?.startsWith('Trash/')
        ? 'permanently deleted'
        : 'moved to Trash';
      const message = `${deletedDocument.isFolder ? 'Folder' : 'File'} ${
        deletedDocument.name
      } ${action}`;
      onSuccess(deletedDocument, message);
    }
  }, [deletedDocuments, document.path, onSuccess]);

  useEffect(() => {
    if (onError && error) {
      console.log('Error Deleting:', error);
      onError(
        error,
        `Unable to delete ${document.isFolder ? 'folder' : 'file'} ${
          document.name
        }, please check your internet connection and try again.`
      );
    }
  }, [document.isFolder, document.name, error, onError]);

  const handleDelete = () => {
    del();
  };

  let title = 'Confirm Move to Trash';
  let message = `Are you sure you want move the ${
    isFolder ? 'folder' : 'file'
  } "${document.name}" and all its content to Trash?`;
  if (document.path?.startsWith('Trash/')) {
    title = 'Confirm Permanent Delete';
    message = `Are you sure you want to permanently delete the ${
      isFolder ? 'folder' : 'file'
    } "${document.name}" and all its content? This action cannot be undone.`;
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='xs'>
      {isLoading && <LinearProgress />}
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          className={classes.deleteButton}
          disabled={isLoading}
          onClick={handleDelete}
          variant='contained'
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(() => ({
  deleteButton: {
    backgroundColor: '#e53935',
    color: '#fff'
  }
}));

export default DocumentDeleteDialog;
